import { legit } from "apex-legit-service";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RootState } from "StoreTypes";
import { LabeledInput, Spinner } from "@apexinternal/ui-components";
import { Button } from "semantic-ui-react";
import {
  Box,
  Center,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  ALL_REQUESTED_PERMISSIONS,
  LEGIT_SERVICE_CONFIG,
} from "../../constants";
import { isApexInternalApp } from "../../utils";
import { LocaleKey, useFormatMessage } from "../../utils/intlHelpers";
import { isPrimitiveEmpty } from "./ApexLogin";

const INVALID_CREDENTIALS = "Incorrect Username/Password";

export interface ApexLegacyLoginProps {
  defaultUsername: string;
  loggingIn: boolean;
  legacyLoginSubmit: (
    username: string,
    password: string,
    isApexUser: boolean
  ) => void;
  loginFailed: boolean;
}

export const ApexLegacyLoginComponent = ({
  defaultUsername,
  loggingIn,
  legacyLoginSubmit,
  loginFailed,
}: ApexLegacyLoginProps) => {
  const formatMessage = useFormatMessage();
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [isApexUser, setApexUser] = useState<boolean>(isApexInternalApp());

  const handleLegacyLogin = (event: React.SyntheticEvent) => {
    setUsernameError(isPrimitiveEmpty(username));
    setPasswordError(isPrimitiveEmpty(password));

    if (!isPrimitiveEmpty(username) && !isPrimitiveEmpty(password)) {
      legacyLoginSubmit(username, password, isApexUser);
    }
  };

  const onUsernameChange = (value: string) => {
    setUsernameError(isPrimitiveEmpty(value));
    setUsername(value);
  };

  const onPasswordChange = (value: string) => {
    setPasswordError(isPrimitiveEmpty(value));
    setPassword(value);
  };

  const isSubmitDisabled =
    loggingIn ||
    usernameError ||
    passwordError ||
    !username.length ||
    !password.length;

  return loggingIn ? (
    <Center>
      <Spinner size="lg" />
    </Center>
  ) : (
    <form onSubmit={handleLegacyLogin}>
      <Stack direction="column" spacing={4}>
        <FormControl isInvalid={usernameError}>
          <LabeledInput
            data-testid="test-id-legacy-username"
            value={username}
            label={formatMessage("login.username" as LocaleKey)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onUsernameChange(e.target.value)
            }
            isRequired
            isInvalid={usernameError}
            fontWeight="bold"
            size="md"
          />
          {usernameError && (
            <FormErrorMessage>
              {formatMessage("login.username.required" as LocaleKey)}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl isInvalid={passwordError}>
          <LabeledInput
            data-testid="test-id-legacy-password"
            label={formatMessage("login.password" as LocaleKey)}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onPasswordChange(e.target.value)
            }
            type="password"
            isRequired
            isInvalid={passwordError}
            fontWeight="bold"
            size="md"
          />
          {passwordError && (
            <FormErrorMessage>
              {formatMessage("login.password.required" as LocaleKey)}
            </FormErrorMessage>
          )}
        </FormControl>
        {isApexInternalApp() && (
          <Checkbox
            fontWeight={400}
            isChecked={isApexUser}
            onChange={(e) => setApexUser(e.target.checked)}
          >
            Apex User
          </Checkbox>
        )}
        {loginFailed && (
          <Box>
            <Text color="apex.orangeRed">{INVALID_CREDENTIALS}</Text>
          </Box>
        )}
        <Button
          variant="primary"
          id="sign-in"
          type="submit"
          isDisabled={isSubmitDisabled}
          width="100%"
        >
          {formatMessage("login.signin" as LocaleKey)}
        </Button>
      </Stack>
    </form>
  );
};

const mapStateToProps = (state: RootState) => {
  const {
    [legit.NAMESPACE]: { error, params },
  }: { [index: string]: any } = state;
  const loginFailed =
    error?.message === "Unauthorized" && params?.apexUser && !!params?.username;
  return { loginFailed };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  legacyLoginSubmit: (
    username: string,
    password: string,
    isApexUser: boolean
  ) =>
    dispatch(
      legit.login(LEGIT_SERVICE_CONFIG, {
        username,
        password,
        apexUser: true,
        permissions: ALL_REQUESTED_PERMISSIONS,
      })
    ),
});

export const ApexLegacyLogin = connect(
  mapStateToProps,
  mapDispatchToProps
)(ApexLegacyLoginComponent);
